import { Container, Typography } from '@cleartrip/ct-design-components'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { motion } from 'framer-motion'
import React, { useState } from 'react'

import { getSrpSearchData } from '@/redux/slices/srpSlice'
import { useAppSelector } from '@/redux/store/hooks'

import { ChevronLeft, ChevronRight } from '@/assets'
import { Stencil } from '@/components/common/Stencil'
import { formatCurrency } from '@/utils'
import { isEmpty } from '@/utils/general/browserHelper'
import { getCheapestCardPrice } from '@/utils/srp/helper'

dayjs.extend(customParseFormat)

/** Fixed height; the row's own `py-2` insets the cells, so the dividers stop short of the edges. */
const ROW = { width: '100%', height: '56px', padding: '0 12px', boxSizing: 'border-box' } as const
const CELL = { rowGap: '2px', height: '100%', position: 'relative' } as const
/** An overlay, not a border: the cell ends 8px above the card, the underline still has to touch its bottom edge. */
const UNDERLINE = {
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '-8px',
    height: '2px',
    borderRadius: '8px 8px 0 0',
    backgroundColor: '#1A1A1A',
  },
} as const
const CELL_SELECTED = { ...CELL, ...UNDERLINE } as const
const CELL_DIVIDED = { ...CELL, borderLeft: '1px solid #E6E6E6' } as const
const CELL_DIVIDED_SELECTED = { ...CELL_SELECTED, borderLeft: '1px solid #E6E6E6' } as const
const CELL_CLASS = 'flex flex-1 flex-column flex-center flex-middle'
const CELL_CLASS_CLICKABLE = `${CELL_CLASS} c-pointer`
const ARROW_COLOR = '#1A1A1A'
/** Arrow click slides the new set of days in from the clicked side. */
const SLIDE_OFFSET = 48
const SLIDE_TRANSITION = { duration: 0.25, ease: [0.4, 0, 0.2, 1] }
const SLIDE_ANIMATE = { x: 0, opacity: 1 }
/** Clip the slide sideways only: the selected underline hangs 8px below the cell. */
const SLIDE_CLIP = { clipPath: 'inset(-8px 0)' }

type DateTabsProps = {
  curDate: string
  tabPriceData: any[]
  journeyType: string
  sortedFilteredCards: any
  onUpdateSelectedDate: (date: string) => void
  handleArrowClick: (direction: string) => void
}

const DateTabs = ({
  curDate,
  handleArrowClick,
  journeyType,
  onUpdateSelectedDate,
  sortedFilteredCards,
  tabPriceData,
}: DateTabsProps) => {
  const { searchData } = useAppSelector(getSrpSearchData)
  const [slideFrom, setSlideFrom] = useState(0)

  const onArrow = (direction: 'left' | 'right') => {
    setSlideFrom(direction === 'right' ? SLIDE_OFFSET : -SLIDE_OFFSET)
    handleArrowClick(direction)
  }
  // Remount on window change (first date) so the slide replays; date selection keeps the key, so no slide.
  const windowKey = tabPriceData[0]?.date
  const slideInitial = { x: slideFrom, opacity: slideFrom ? 0 : 1 }

  /** The selected day's price comes from the cards on screen, not the fare calendar; null keeps the seed. */
  const priceForSelectedDate = () =>
    isEmpty(searchData) ? null : getCheapestCardPrice(sortedFilteredCards?.[journeyType === 'onward' ? 0 : 1], searchData)

  return (
    <Container className='flex flex-middle ba-solid bc-line-500 bg-neutral-100 br-12 py-2' css={ROW}>
      <ChevronLeft
        className='c-pointer'
        data-direction='left'
        stroke={ARROW_COLOR}
        width='20'
        onClick={() => onArrow('left')}
      />

      <Container className='flex flex-1 h-100p' css={SLIDE_CLIP}>
        <motion.div
          key={windowKey}
          animate={SLIDE_ANIMATE}
          className='flex flex-1 h-100p'
          initial={slideInitial}
          transition={SLIDE_TRANSITION}
        >
        {tabPriceData.map((element, index) => {
          const isSelected = element.date === dayjs(curDate, 'DD/MM/YYYY').format('DD-MM-YYYY')
          const cardPrice = isSelected ? priceForSelectedDate() : null
          const pr = cardPrice ?? element.pr

          const unselectedPriceColor = element.isCheapest ? 'success' : 'subheading'
          const priceColor = isSelected ? 'heading' : unselectedPriceColor
          const dividedCellCss = isSelected ? CELL_DIVIDED_SELECTED : CELL_DIVIDED
          const firstCellCss = isSelected ? CELL_SELECTED : CELL
          const cellCss = index ? dividedCellCss : firstCellCss
          const cellClassName = isSelected ? CELL_CLASS : CELL_CLASS_CLICKABLE
          const dateLabel = dayjs(element.date, 'DD-MM-YYYY').format('ddd, DD MMM')
          const priceText = pr === null || pr === '-' ? '-' : formatCurrency(pr)

          return (
            <Container
              key={element.date}
              className={cellClassName}
              css={cellCss}
              onClick={() => !isSelected && onUpdateSelectedDate(element.date)}
            >
              <Typography color={isSelected ? 'heading' : 'subheading'} variant='HM4'>
                {dateLabel}
              </Typography>
              {pr === null ? (
                <Stencil shimmer borderRadius='4px' height='16px' width='56px' />
              ) : (
                <Typography color={priceColor} variant='B3'>
                  {priceText}
                </Typography>
              )}
            </Container>
          )
        })}
        </motion.div>
      </Container>

      <ChevronRight
        className='c-pointer'
        data-direction='right'
        stroke={ARROW_COLOR}
        width='20'
        onClick={() => onArrow('right')}
      />
    </Container>
  )
}

export default DateTabs
